const activeLocks = new Set();
let lockedScrollY = 0;
let restoreFrame = null;

const canUseDOM = () => typeof document !== "undefined";

const getElements = () => ({
  root: document.documentElement,
  body: document.body,
});

const cancelPendingRestore = () => {
  if (typeof window === "undefined" || restoreFrame === null) return;
  window.cancelAnimationFrame(restoreFrame);
  restoreFrame = null;
};

const suppressSmoothScroll = () => {
  const { root, body } = getElements();
  root.classList.add("scroll-lock-no-smooth");
  body.classList.add("scroll-lock-no-smooth");
  root.style.scrollBehavior = "auto";
  body.style.scrollBehavior = "auto";
};

const restoreSmoothScroll = () => {
  const { root, body } = getElements();
  root.classList.remove("scroll-lock-no-smooth");
  body.classList.remove("scroll-lock-no-smooth");
  root.style.scrollBehavior = "";
  body.style.scrollBehavior = "";
};

const shouldUseFixedBodyLock = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  const isCoarsePointer = typeof window.matchMedia === "function"
    && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const ua = navigator.userAgent || "";
  const isIOS = /iP(ad|hone|od)/i.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  return isCoarsePointer || isIOS;
};

const applyLockStyles = () => {
  const { root, body } = getElements();
  cancelPendingRestore();
  suppressSmoothScroll();
  root.style.overflow = "hidden";
  root.style.overscrollBehavior = "none";
  body.style.overflow = "hidden";
  body.style.overscrollBehavior = "none";

  if (shouldUseFixedBodyLock()) {
    body.style.position = "fixed";
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  }
};

const clearLockStyles = () => {
  const { root, body } = getElements();
  root.style.overflow = "";
  root.style.overscrollBehavior = "";
  root.style.touchAction = "";
  root.style.pointerEvents = "";
  body.style.overflow = "";
  body.style.overscrollBehavior = "";
  body.style.touchAction = "";
  body.style.pointerEvents = "";
  body.style.position = "";
  body.style.top = "";
  body.style.left = "";
  body.style.right = "";
  body.style.width = "";
};

const getStyleSnapshot = () => {
  if (!canUseDOM()) {
    return {
      htmlOverflow: "",
      bodyOverflow: "",
      htmlOverscrollBehavior: "",
      bodyOverscrollBehavior: "",
      htmlTouchAction: "",
      bodyTouchAction: "",
      htmlPointerEvents: "",
      bodyPointerEvents: "",
      bodyPosition: "",
      bodyTop: "",
      lockedScrollY: 0,
    };
  }

  const { root, body } = getElements();
  return {
    htmlOverflow: root.style.overflow,
    bodyOverflow: body.style.overflow,
    htmlOverscrollBehavior: root.style.overscrollBehavior,
    bodyOverscrollBehavior: body.style.overscrollBehavior,
    htmlTouchAction: root.style.touchAction,
    bodyTouchAction: body.style.touchAction,
    htmlPointerEvents: root.style.pointerEvents,
    bodyPointerEvents: body.style.pointerEvents,
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    lockedScrollY,
  };
};

export const lockPageScroll = (lockId = "global") => {
  if (!canUseDOM()) return;

  if (activeLocks.size === 0) {
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
  }

  activeLocks.add(lockId);
  applyLockStyles();
};

export const unlockPageScroll = (lockId = "global") => {
  if (!canUseDOM()) return;

  activeLocks.delete(lockId);
  if (activeLocks.size > 0) {
    applyLockStyles();
    return;
  }

  clearLockStyles();
  restoreScrollPosition();
};

export const forceUnlockPageScroll = () => {
  if (!canUseDOM()) return;
  activeLocks.clear();
  clearLockStyles();
  restoreScrollPosition();
};

export const syncPageScrollLock = () => {
  if (!canUseDOM()) return;
  if (activeLocks.size === 0) {
    clearLockStyles();
    restoreScrollPosition();
    return;
  }

  applyLockStyles();
};

export const getScrollLockState = () => ({
  activeLocks: Array.from(activeLocks),
  lockCount: activeLocks.size,
  usesFixedBodyLock: shouldUseFixedBodyLock(),
  ...getStyleSnapshot(),
});

const restoreScrollPosition = () => {
  cancelPendingRestore();

  if (!lockedScrollY) {
    restoreSmoothScroll();
    return;
  }

  const targetY = lockedScrollY;
  window.scrollTo(0, targetY);

  restoreFrame = window.requestAnimationFrame(() => {
    window.scrollTo(0, targetY);
    restoreFrame = window.requestAnimationFrame(() => {
      window.scrollTo(0, targetY);
      lockedScrollY = 0;
      restoreSmoothScroll();
      restoreFrame = null;
    });
  });
};

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__scrollLockDebug = () => getScrollLockState();
}

